/**
 * Group Management Tabs Component
 * Handles tab navigation and content rendering for different group views
 */

import {
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../hooks/permissions/usePermissions';

import { GroupList } from './GroupList';
import { GroupRequestsList } from './GroupRequestsList';

enum TabValue {
  ALL = 0,
  MY_GROUPS = 1,
  LOCAL = 2,
  EXTERNAL = 3,
  PENDING_REQUESTS = 4,
}

interface Group {
  id: string;
  name?: string;
  displayName?: string;
  source: string;
  [key: string]: any;
}

interface GroupManagementTabsProps {
  hasGroupManagementPermission: boolean;
  allGroups: Group[];
  myGroups: Group[];
  activeTab: number;
  onTabChange: (tab: number) => void;
  onGroupClick: (group: Group) => void;
  onManageMembers?: (group: Group) => void;
  selectedOrgId?: string;
  /**
   * Whether to show pending requests tab (moderator/admin only)
   */
  showPendingRequestsTab?: boolean;
  /**
   * Callback when a request is processed (approved/rejected)
   */
  onRequestProcessed?: () => void;
}

export const GroupManagementTabs: React.FC<GroupManagementTabsProps> = ({
  hasGroupManagementPermission,
  allGroups,
  myGroups,
  activeTab,
  onTabChange,
  onGroupClick,
  onManageMembers,
  selectedOrgId,
  showPendingRequestsTab = false,
  onRequestProcessed,
}) => {
  const { t } = useTranslation();
  const { user } = usePermissions();
  // Multi-org feature - currently superadmin only (future: can.manageAllOrganizations)
  const isSuperadmin = user?.role === 'superadmin';

  const getFilteredGroups = (filter: 'local' | 'external') => {
    return allGroups.filter(g =>
      filter === 'local' ? g.source === 'local' : g.source !== 'local'
    );
  };

  // Simple view for regular users - just show their groups
  if (!hasGroupManagementPermission || (isSuperadmin && !selectedOrgId)) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ height: '100%', overflow: 'auto' }}>
          <GroupList
            groups={myGroups}
            onGroupClick={onGroupClick}
            title=""
            showSource={true}
            emptyMessage={t('groups.emptyStates.contactAdminToJoin')}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box borderBottom={1} borderColor="divider">
        <Tabs value={activeTab} onChange={(_, value) => onTabChange(value)}>
          <Tab label={t('groups.tabs.allGroups')} />
          <Tab label={t('groups.tabs.myGroups')} />
          <Tab label={t('groups.tabs.localGroups')} />
          <Tab label={t('groups.tabs.externalGroups')} />
          {showPendingRequestsTab && (
            <Tab label={t('groups.tabs.pendingRequests')} />
          )}
        </Tabs>
      </Box>

      <CardContent sx={{ flexGrow: 1, overflow: 'auto' }}>
        {activeTab === TabValue.ALL && (
          <GroupList
            groups={allGroups}
            onGroupClick={onGroupClick}
            onManageMembers={onManageMembers}
            title={t('groups.tabs.allGroups')}
            showSource={true}
            canManageGroups={hasGroupManagementPermission}
          />
        )}
        {activeTab === TabValue.MY_GROUPS && (
          <GroupList
            groups={myGroups}
            onGroupClick={onGroupClick}
            onManageMembers={onManageMembers}
            title={t('groups.tabs.myGroups')}
            showSource={true}
            emptyMessage={t('groups.emptyStates.notMemberOfAnyGroups')}
            canManageGroups={hasGroupManagementPermission}
          />
        )}
        {activeTab === TabValue.LOCAL && (
          <GroupList
            groups={getFilteredGroups('local')}
            onGroupClick={onGroupClick}
            onManageMembers={onManageMembers}
            title={t('groups.tabs.localGroups')}
            showSource={false}
            emptyMessage={t('groups.emptyStates.noLocalGroups')}
            canManageGroups={hasGroupManagementPermission}
          />
        )}
        {activeTab === TabValue.EXTERNAL && (
          <GroupList
            groups={getFilteredGroups('external')}
            onGroupClick={onGroupClick}
            onManageMembers={onManageMembers}
            title={t('groups.tabs.externalGroups')}
            showSource={true}
            emptyMessage={t('groups.emptyStates.noExternalGroups')}
            canManageGroups={false} // External groups cannot be managed
          />
        )}
        {activeTab === TabValue.PENDING_REQUESTS && showPendingRequestsTab && (
          <GroupRequestsList onRequestProcessed={onRequestProcessed} />
        )}
      </CardContent>
    </Card>
  );
};